import { useEffect, useMemo, useState } from 'react'

import { addTranslationForLanguage, setActiveLanguage } from 'react-localize-redux'
import { bindActionCreators } from 'redux'
import { useDispatch } from 'react-redux'
import { isFunction } from 'lodash'
import { useLocation } from 'react-router-dom'

import { getIdOrNull } from '../../utils'
import { Properties } from '../../utils/entities'
import { LanguageLocales } from '../../utils/constants'
import { setLanguage } from '../../utils/locale'
import {
  getInstitutionGroupInternshipRequestPortal,
  getInstitutionInternshipRequestPortal
} from '../../utils/api/internshipRequestPortal'

const PortalAccess = Object.freeze({
  INSTITUTION: 'INSTITUTION',
  INSTITUTION_GROUP: 'INSTITUTION_GROUP'
})

const UriParams = Object.freeze({
  ACCESS_KEY: 'portal-access-key',
  INSTITUTION: 'institution',
  INSTITUTION_GROUP: 'institution-group'
})

const Errors = Object.freeze({
  BROKEN: 'internship_request_portal.broken_link',
  MALFORMED: 'internship_request_portal.malformed_link'
})

const BROKEN_LINK_CODES = new Set(['ACCESS_KEY_NOT_PROVIDED', 'WRONG_ACCESS_KEY'])

const resolveErrorKey = code => BROKEN_LINK_CODES.has(code) ? Errors.BROKEN : Errors.MALFORMED

const KNOWN_LANGUAGE_TAGS = new Set(Object.values(LanguageLocales))

/** An unknown tag leaves no language active at all, and the next translation call white-screens a public page. */
const resolveLanguageTag = tag => {
  const normalized = (tag ?? '').toLowerCase()

  return KNOWN_LANGUAGE_TAGS.has(normalized) ? normalized : LanguageLocales.FR_BE
}

const usePortalResolution = (isStudentPortal = false) => {
  const location = useLocation()
  const dispatch = useDispatch()

  const dispatchSetActiveLanguage = useMemo(() => bindActionCreators(setActiveLanguage, dispatch), [dispatch])
  const dispatchAddTranslationForLanguage = useMemo(() => bindActionCreators(addTranslationForLanguage, dispatch), [dispatch])

  const [loadedLanguageTag, setLoadedLanguageTag] = useState('')
  const [portalData, setPortalData] = useState({ institution: null, institutionGroup: null, accessKey: null })
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (!(getIdOrNull(portalData.institution) || getIdOrNull(portalData.institutionGroup))) {
      const searchParams = new URLSearchParams(location.search)
      const accessKey = searchParams.get(UriParams.ACCESS_KEY)

      const config = { request: null, property: null }

      if (location.search.includes(UriParams[PortalAccess.INSTITUTION_GROUP])) {
        config.property = PortalAccess.INSTITUTION_GROUP
        config.request = getInstitutionGroupInternshipRequestPortal
      } else if (location.search.includes(UriParams[PortalAccess.INSTITUTION])) {
        config.property = PortalAccess.INSTITUTION
        config.request = getInstitutionInternshipRequestPortal
      }

      if (isFunction(config.request) && config.property !== null) {
        const { request, property } = config

        request(searchParams.get(UriParams[property]), accessKey, isStudentPortal)
          .then(({ data }) => setPortalData(portalData => ({ ...portalData, [Properties[property]]: data, accessKey })))
          .catch(reason => reason.json()
            .then(({ message }) => setErrorMessage(resolveErrorKey(message)))
            .catch(() => setErrorMessage(Errors.BROKEN)))
      } else {
        setErrorMessage(Errors.MALFORMED)
      }
    }

    const language = resolveLanguageTag(
      portalData.institution?.languageLocale?.languageTag ??
      portalData.institutionGroup?.languageLocale?.languageTag
    )

    setLanguage({
      language,
      addTranslationForLanguage: dispatchAddTranslationForLanguage,
      setActiveLanguage: dispatchSetActiveLanguage
    }).then(() => setLoadedLanguageTag(language))
  }, [
    portalData.institution, portalData.institutionGroup, location.search, isStudentPortal,
    dispatchAddTranslationForLanguage, dispatchSetActiveLanguage
  ])

  const isResolved = getIdOrNull(portalData.institution) !== null || getIdOrNull(portalData.institutionGroup) !== null

  return { portalData, errorMessage, loadedLanguageTag, isResolved }
}

export default usePortalResolution
